import { ArrowsLeftRight, Binoculars, Cube, Trophy } from "@phosphor-icons/react"
import Image from "next/image"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"
import StablecoinIssuersIcon from "./StablecoinIssuersIcon"
import { useTreemapData } from "./useTreemapData"

const STATIC_ASSETS_BASE_URL = "https://static1.tokenterminal.com/"

const SECTOR_STYLES = {
  "stablecoin-issuers": {
    container: "bg-[hsl(var(--chart-1)/0.15)]",
    project: "bg-[hsl(var(--chart-1)/0.35)]",
    projectHover: "hover:bg-[hsl(var(--chart-1)/0.45)]",
  },
  "blockchains-l1": {
    container: "bg-[hsl(var(--chart-2)/0.15)]",
    project: "bg-[hsl(var(--chart-2)/0.30)]",
    projectHover: "hover:bg-[hsl(var(--chart-2)/0.40)]",
  },
  exchange: {
    container: "bg-[hsl(var(--chart-3)/0.15)]",
    project: "bg-[hsl(var(--chart-3)/0.30)]",
    projectHover: "hover:bg-[hsl(var(--chart-3)/0.40)]",
  },
  "liquid-staking": {
    container: "bg-[hsl(var(--chart-12)/0.15)]",
    project: "bg-[hsl(var(--chart-12)/0.30)]",
    projectHover: "hover:bg-[hsl(var(--chart-12)/0.40)]",
  },
} as const

type SectorId = keyof typeof SECTOR_STYLES

const SECTOR_ICONS: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  "stablecoin-issuers": StablecoinIssuersIcon,
  "blockchains-l1": Cube,
  exchange: ArrowsLeftRight,
  "liquid-staking": Trophy,
}

function formatValue(value: number): string {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`
  }
  return `$${value.toFixed(0)}`
}

function FallbackLogo({ name, size = 16 }: { name: string; size?: number }) {
  const colors = [
    "bg-cyan-400",
    "bg-pink-400",
    "bg-orange-400",
    "bg-emerald-400",
    "bg-violet-400",
    "bg-red-400",
    "bg-yellow-400",
    "bg-blue-400",
  ]
  const colorIndex =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length

  return (
    <div
      className={`${colors[colorIndex]} rounded-full flex items-center justify-center shrink-0`}
      style={{ width: size, height: size }}
    >
      <span className="text-[8px] font-bold text-white/90">{name.charAt(0).toUpperCase()}</span>
    </div>
  )
}

interface ProjectLogoProps {
  src: string
  name: string
  size?: number
}

function ProjectLogo({ src, name, size = 16 }: ProjectLogoProps) {
  if (!src) {
    return <FallbackLogo name={name} size={size} />
  }

  const logoUrl = new URL(src, STATIC_ASSETS_BASE_URL).href

  return (
    <div
      className="rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <Image
        src={logoUrl}
        alt={name}
        width={size}
        height={size}
        className="object-cover"
        onError={(e) => {
          // Hide the image and show fallback
          const target = e.currentTarget
          target.style.display = "none"
          const parent = target.parentElement
          if (parent) {
            parent.innerHTML = `<div class="w-full h-full rounded-full bg-cyan-400 flex items-center justify-center"><span class="text-[8px] font-bold text-white/90">${name.charAt(0).toUpperCase()}</span></div>`
          }
        }}
      />
    </div>
  )
}

interface ProjectNodeProps {
  project: {
    id: string
    name: string
    logo: string
    value: number
    percentOfTotal: number
  }
  sectorId: string
  showLabel?: boolean
  showValue?: boolean
  className?: string
}

function ProjectNode({
  project,
  sectorId,
  showLabel = true,
  showValue = true,
  className = "",
}: ProjectNodeProps) {
  const styles = SECTOR_STYLES[sectorId as SectorId] || SECTOR_STYLES["stablecoin-issuers"]

  return (
    <div
      className={`${styles.project} ${styles.projectHover} rounded-sm transition-colors duration-150 p-2 flex flex-col justify-end h-full min-h-0 overflow-hidden ${className}`}
    >
      <div className="mb-auto">
        <ProjectLogo src={project.logo} name={project.name} size={16} />
      </div>
      {showLabel && (
        <div className="mt-auto pt-1">
          <p className="text-[10px] text-fg-default font-medium truncate leading-tight">
            {project.name}
          </p>
          {showValue && (
            <p className="text-[9px] text-fg-default/70 truncate leading-tight">
              {formatValue(project.value)} ({project.percentOfTotal.toFixed(1)}
              %)
            </p>
          )}
        </div>
      )}
    </div>
  )
}

interface SectorHeaderProps {
  sectorId: string
  name: string
}

function SectorHeader({ sectorId, name }: SectorHeaderProps) {
  const SectorIcon = SECTOR_ICONS[sectorId] || Cube

  return (
    <div className="flex items-center gap-1.5 px-1 shrink-0">
      <SectorIcon className="text-fg-default/50" size={12} />
      <span className="text-xs text-fg-default/80 truncate">{name}</span>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse h-full">
      <div className="flex gap-1 h-full">
        <div className="flex-[1.5] bg-neutral-800/20 rounded-lg" />
        <div className="flex-1 bg-neutral-800/20 rounded-lg" />
        <div className="flex-[0.7] flex flex-col gap-1">
          <div className="flex-1 bg-neutral-800/20 rounded-lg" />
          <div className="flex-1 bg-neutral-800/20 rounded-lg" />
        </div>
        <div className="flex-[0.55] flex flex-col gap-1">
          <div className="flex-1 bg-neutral-800/20 rounded-lg" />
          <div className="flex gap-1 h-[70px]">
            <div className="flex-1 bg-neutral-800/20 rounded-lg" />
            <div className="flex-1 bg-neutral-800/20 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  )
}

interface SectorBlockProps {
  sector: {
    id: string
    name: string
    projects: {
      id: string
      name: string
      logo: string
      value: number
      percentOfTotal: number
    }[]
  }
  layout: "large" | "tall" | "medium" | "small" | "tiny"
}

function SectorBlock({ sector, layout }: SectorBlockProps) {
  const styles = SECTOR_STYLES[sector.id as SectorId] || SECTOR_STYLES["stablecoin-issuers"]

  if (layout === "large") {
    return (
      <div className={`h-full ${styles.container} rounded-lg p-1.5 flex flex-col`}>
        <SectorHeader sectorId={sector.id} name={sector.name} />
        <div className="flex-1 grid grid-cols-2 gap-1 mt-1.5 min-h-0">
          {sector.projects[0] && (
            <ProjectNode project={sector.projects[0]} sectorId={sector.id} className="row-span-2" />
          )}
          {sector.projects[1] && <ProjectNode project={sector.projects[1]} sectorId={sector.id} />}
          <div className="flex gap-1">
            {sector.projects[2] && (
              <ProjectNode
                project={sector.projects[2]}
                sectorId={sector.id}
                showValue={false}
                className="flex-1"
              />
            )}
            {sector.projects[3] && (
              <ProjectNode
                project={sector.projects[3]}
                sectorId={sector.id}
                showValue={false}
                className="flex-1"
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (layout === "tall") {
    return (
      <div className={`h-full ${styles.container} rounded-lg p-1.5 flex flex-col`}>
        <SectorHeader sectorId={sector.id} name={sector.name} />
        <div className="flex-1 flex flex-col gap-1 mt-1.5 min-h-0">
          {sector.projects[0] && (
            <ProjectNode project={sector.projects[0]} sectorId={sector.id} className="flex-[1.5]" />
          )}
          {sector.projects[1] && (
            <ProjectNode project={sector.projects[1]} sectorId={sector.id} className="flex-1" />
          )}
          <div className="flex gap-1 flex-[0.6]">
            {sector.projects[2] && (
              <ProjectNode
                project={sector.projects[2]}
                sectorId={sector.id}
                showValue={false}
                className="flex-1"
              />
            )}
            {sector.projects[3] && (
              <div
                className={`${styles.project} rounded-sm flex-1 flex items-center justify-center`}
              >
                <ProjectLogo
                  src={sector.projects[3].logo}
                  name={sector.projects[3].name}
                  size={14}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (layout === "medium") {
    return (
      <div className={`h-full ${styles.container} rounded-lg p-1.5 flex flex-col`}>
        <SectorHeader sectorId={sector.id} name={sector.name} />
        <div className="flex-1 grid grid-cols-2 gap-1 mt-1.5 min-h-0">
          {sector.projects[0] && <ProjectNode project={sector.projects[0]} sectorId={sector.id} />}
          <div className="flex flex-col gap-1">
            {sector.projects[1] && (
              <ProjectNode
                project={sector.projects[1]}
                sectorId={sector.id}
                showValue={false}
                className="flex-1"
              />
            )}
            {sector.projects[2] && (
              <ProjectNode
                project={sector.projects[2]}
                sectorId={sector.id}
                showValue={false}
                className="flex-1"
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (layout === "small") {
    return (
      <div className={`h-full ${styles.container} rounded-lg p-1.5 flex flex-col`}>
        <SectorHeader sectorId={sector.id} name={sector.name} />
        <div className="flex-1 flex flex-col gap-1 mt-1.5 min-h-0">
          {sector.projects[0] && (
            <ProjectNode project={sector.projects[0]} sectorId={sector.id} className="flex-1" />
          )}
          <div className="flex gap-1 flex-[0.5]">
            {sector.projects[1] && (
              <div
                className={`${styles.project} rounded-sm flex-1 flex items-center justify-center`}
              >
                <ProjectLogo
                  src={sector.projects[1].logo}
                  name={sector.projects[1].name}
                  size={12}
                />
              </div>
            )}
            {sector.projects[2] && (
              <div
                className={`${styles.project} rounded-sm flex-1 flex items-center justify-center`}
              >
                <ProjectLogo
                  src={sector.projects[2].logo}
                  name={sector.projects[2].name}
                  size={12}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-full ${styles.container} rounded-lg p-1 flex flex-col overflow-hidden`}>
      <div className="flex items-center gap-1 px-0.5 shrink-0">
        {SECTOR_ICONS[sector.id] && (
          <span className="text-fg-default/50">
            {(() => {
              const Icon = SECTOR_ICONS[sector.id]
              return <Icon size={10} />
            })()}
          </span>
        )}
        <span className="text-[10px] text-fg-default/80 truncate leading-tight">{sector.name}</span>
      </div>
      <div className="flex-1 flex items-center justify-center gap-1">
        {sector.projects.slice(0, 2).map((project) => (
          <div
            key={project.id}
            className={`${styles.project} rounded-xs w-4 h-4 flex items-center justify-center`}
          >
            <ProjectLogo src={project.logo} name={project.name} size={10} />
          </div>
        ))}
      </div>
    </div>
  )
}

export function MasonryTreeMap() {
  const { sectors, isLoading } = useTreemapData()

  const showLoading = isLoading && sectors.length === 0
  const getSectorById = (id: string) => sectors.find((s) => s.id === id)

  const stablecoinSector = getSectorById("stablecoin-issuers")
  const blockchainsSector = getSectorById("blockchains-l1")
  const exchangeSector = getSectorById("exchange")
  const liquidStakingSector = getSectorById("liquid-staking")

  return (
    <MasonryWidget
      href={PRODUCTS.EXPLORER_ROUTE}
      icon={Binoculars}
      productName="Explorer"
      widgetName="Market sectors"
      description="Visualize how fees and revenue are distributed across market sectors like stablecoins, L1 blockchains, exchanges, and more."
      height={500}
      featuredVisual={
        <div
          className="relative bg-bg-surface border-t border-l border-solid border-border-default rounded-tl-xl flex-1 min-h-0 pt-3 pl-4"
          style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
        >
          <div className="pb-3 shrink-0">
            <h3 className="text-sm font-medium text-fg-default">Fees by market sector</h3>
          </div>

          <div className="pr-2.5 pb-2 flex-1 min-h-0 h-[250px] w-[600px]">
            {showLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="flex gap-1.5 h-full">
                <div className="flex-[1.8] min-w-0">
                  {stablecoinSector && <SectorBlock sector={stablecoinSector} layout="large" />}
                </div>

                <div className="flex-[1.2] min-w-0">
                  {blockchainsSector && <SectorBlock sector={blockchainsSector} layout="tall" />}
                </div>

                <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                  {exchangeSector && (
                    <div className="flex-1">
                      <SectorBlock sector={exchangeSector} layout="medium" />
                    </div>
                  )}
                  {liquidStakingSector && (
                    <div className="flex-1">
                      <SectorBlock sector={liquidStakingSector} layout="medium" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      }
    />
  )
}
